import React, { useCallback, useMemo } from 'react';
import cc from 'classcat';
import { WMButton, WMButtonVariant, WMIconArrowRight, WMSelect, WMSelectOption, WMSelectValue } from '@walkme/wm-ui';
import classes from './DayPickerCaption.module.scss';
import { CaptionProps, useDayPicker, useNavigation } from 'react-day-picker';
import { setMonth as setDateMonth, setYear as setDateYear } from 'date-fns';
import { getMonths, getYears, getFnsLocale } from '../helpers/utils';
import useSelector from '../../../../../../infrastructure/useSelector';

export default function DayPickerCaption({
    displayMonth,
    fromMonth,
    toMonth,
    dataTesid
}: CaptionProps & { fromMonth: Date; toMonth: Date; dataTesid?: string }): JSX.Element {
    const { goToMonth, nextMonth, previousMonth } = useNavigation();
    const {
        formatters: { formatMonthCaption, formatYearCaption }
    } = useDayPicker();

    const languageCode = useSelector(state => state.ui.languageCode);
    const locale = getFnsLocale(languageCode);

    const monthsOptions = useMemo(
        () =>
            getMonths().map((month: Date) => ({
                value: month.getMonth(),
                label: formatMonthCaption(month, { locale })
            })),
        [formatMonthCaption, locale]
    );

    const yearsOptions = useMemo(
        () =>
            getYears(fromMonth, toMonth).map((year: Date) => ({
                value: year.getFullYear(),
                label: formatYearCaption(year, { locale })
            })),
        [formatYearCaption, fromMonth, locale, toMonth]
    );

    const monthSelectionValue = useMemo(() => monthsOptions.find(({ value }) => displayMonth.getMonth() === value), [displayMonth, monthsOptions]);
    const yearSelectionValue = useMemo(() => yearsOptions.find(({ value }) => displayMonth.getFullYear() === value), [displayMonth, yearsOptions]);

    const onMonthChange = useCallback(
        (option: WMSelectValue) => {
            const newMonth = setDateMonth(new Date(displayMonth), Number((option as WMSelectOption).value));

            goToMonth(newMonth);
        },
        [goToMonth, displayMonth]
    );

    const onYearChange = useCallback(
        (option: WMSelectValue) => {
            const newMonth = setDateYear(new Date(displayMonth), Number((option as WMSelectOption).value));

            goToMonth(newMonth);
        },
        [goToMonth, displayMonth]
    );

    const isNextMonthDisabled = useMemo(() => !nextMonth || nextMonth.getFullYear() > yearsOptions[yearsOptions.length - 1].value, [
        nextMonth,
        yearsOptions
    ]);

    const isPrevMonthDisabled = useMemo(() => !previousMonth || previousMonth.getFullYear() < yearsOptions[0].value, [previousMonth, yearsOptions]);

    return (
        <div className={classes.dateTimeCaption}>
            <WMButton
                disabled={isPrevMonthDisabled}
                classes={{ root: classes.dateTimeCaptionBtn }}
                onClick={() => previousMonth && goToMonth(previousMonth)}
                variant={WMButtonVariant.Text}
                isIconButton
                iconComponent={<WMIconArrowRight className={classes.rotateArrow} />}
            />

            <div className={classes.dateSelectionWrapper}>
                <WMSelect
                    className={classes.dateSelection}
                    options={monthsOptions}
                    value={monthSelectionValue}
                    onChange={onMonthChange}
                    isSearchable={false}
                    testKey={`${dataTesid}-month-selection`}
                />
                <WMSelect
                    className={cc([classes.dateSelection, classes.yearSelection])}
                    options={yearsOptions}
                    value={yearSelectionValue}
                    onChange={onYearChange}
                    isSearchable={false}
                    testKey={`${dataTesid}-year-selection`}
                />
            </div>

            <WMButton
                disabled={isNextMonthDisabled}
                classes={{ root: classes.dateTimeCaptionBtn }}
                onClick={() => nextMonth && goToMonth(nextMonth)}
                variant={WMButtonVariant.Text}
                isIconButton
                iconComponent={<WMIconArrowRight />}
            />
        </div>
    );
}
